/**
 * Gender-aware filtering for event recommendations.
 * Hides events explicitly marked for the other gender (e.g. "Women's Golf" from
 * a male resident). Events naming both genders, or with no gender marker at all,
 * are shown to everyone. Unknown/missing resident gender never filters — a lookup
 * failure must degrade to "show everything", not "hide things silently".
 */

export type NormalizedGender = 'male' | 'female' | null;

// \b treats the apostrophe as a non-word boundary, so \bwomen\b already matches
// inside "women's" and "girls'" without listing the possessive forms separately.
// Plain informal spellings without an apostrophe ("womens", "mens") don't get that
// boundary for free, so those are listed explicitly.
const WOMEN_MARKER_RE = /\b(women|womens|ladies|lady|girls|females?|woman)\b/i;
const MEN_MARKER_RE = /\b(men|mens|gentlemen|gents|boys|males?|man)\b/i;

/** 'Male'/'M'/'male' → 'male'; 'Female'/'F'/'W'/'Woman' → 'female'; anything else/undefined → null */
export function normalizeGender(raw: unknown): NormalizedGender {
  if (typeof raw !== 'string') return null;
  const value = raw.trim().toLowerCase();
  if (value === 'male' || value === 'm') return 'male';
  if (value === 'female' || value === 'f' || value === 'w' || value === 'woman') return 'female';
  return null;
}

/** True if the title contains a women-only marker word (case-insensitive, word-boundary). */
export function hasWomenMarker(title: string): boolean {
  return WOMEN_MARKER_RE.test(title);
}

/** True if the title contains a men-only marker word (case-insensitive, word-boundary). */
export function hasMenMarker(title: string): boolean {
  return MEN_MARKER_RE.test(title);
}

/**
 * Filter activities by resident gender. null gender → returns input unchanged.
 * An event is hidden from males iff it has a women marker AND no men marker;
 * hidden from females iff it has a men marker AND no women marker.
 * Untitled events are never filtered.
 */
export function filterEventsForGender<T extends { title?: string }>(
  events: T[],
  gender: NormalizedGender
): T[] {
  if (gender === null) return events;

  return events.filter((event) => {
    const title = event.title;
    if (!title) return true;

    const hasWomen = hasWomenMarker(title);
    const hasMen = hasMenMarker(title);

    if (gender === 'male' && hasWomen && !hasMen) return false;
    if (gender === 'female' && hasMen && !hasWomen) return false;
    return true;
  });
}
